import { useWorkerProfile, legacyProfileResponse } from 'src/workspaces/workerProfile/WorkerProfileContext';
import React from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Rating,
  Stack,
  Typography,
  Chip
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getContractorReviewSummary } from 'src/helper/backend_helper';

/**
 * Aggregated bilateral-review summary for a contractor (#324).
 *
 * Mirrors the mobile profile card — averages per dimension + overall +
 * total review count + rebook %. Individual review notes are intentionally
 * not surfaced here; the planning doc emphasises improvement-oriented
 * presentation. Admins who need raw notes can drill into a specific shift.
 */
export interface ContractorRatingSummaryProps {
  userId: number | string;
}

interface AggregateShape {
  averages: {
    punctuality: number | null;
    skillFit: number | null;
    professionalism: number | null;
    overall: number | null;
  };
  rehireRate: number | null;
  totalReviews: number;
}

const formatRating = (value: number | null | undefined): string => {
  if (value == null) return '—';
  return value.toFixed(1);
};

const ContractorRatingSummary: React.FC<ContractorRatingSummaryProps> = ({
  userId
}) => {
  const scope = useWorkerProfile();
  const { data, isLoading, isError } = useQuery(
    ['contractor-review-summary', scope?.companyId, scope?.revision, userId],
    () => scope ? legacyProfileResponse(scope.data.rating) : getContractorReviewSummary(userId),
    {
      enabled: Boolean(userId),
      retry: false,
      staleTime: 60_000
    }
  );

  if (!userId) return null;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (isError) return null;

  const aggregate: AggregateShape | undefined = data?.data?.data?.aggregate;
  const totalReviews = aggregate?.totalReviews ?? 0;
  const overall = aggregate?.averages?.overall ?? null;

  return (
    <Paper className={scope ? "worker-rating-summary" : undefined} elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1.5}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Contractor rating
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Aggregated across recent shifts. Individual reviews stay private.
          </Typography>
        </Box>
        <Chip
          size="small"
          label={`${totalReviews} review${totalReviews === 1 ? '' : 's'}`}
          variant="outlined"
        />
      </Stack>

      {totalReviews === 0 ? (
        <Typography variant="body2" color="text.secondary" mt={1.5}>
          No bilateral ratings yet. They appear once admins/leads start rating
          completed shifts.
        </Typography>
      ) : (
        <Stack spacing={1.5} mt={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Rating value={overall ?? 0} precision={0.1} readOnly />
            <Typography variant="h6" fontWeight={700}>
              {formatRating(overall)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              overall
            </Typography>
          </Stack>
          <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
            <DimensionRow
              label="Punctuality"
              value={aggregate?.averages?.punctuality}
            />
            <DimensionRow
              label="Skill fit"
              value={aggregate?.averages?.skillFit}
            />
            <DimensionRow
              label="Professionalism"
              value={aggregate?.averages?.professionalism}
            />
            {aggregate?.rehireRate != null ? (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Rebook preference
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {Math.round((aggregate.rehireRate ?? 0) * 100)}%
                </Typography>
              </Box>
            ) : null}
          </Stack>
        </Stack>
      )}
    </Paper>
  );
};

const DimensionRow: React.FC<{ label: string; value: number | null | undefined }> = ({
  label,
  value
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {formatRating(value)}
    </Typography>
  </Box>
);

export default ContractorRatingSummary;
